/**
 * Standard API Client for Marketplace Frontend
 *
 * Centralizes API call patterns and error handling across the application.
 *
 * Standard Response Format:
 * - Success: { data: <payload> } with HTTP status 200-299
 * - Error: { error: <message> } with appropriate HTTP error status
 *
 * Usage:
 * - api.get('/endpoint').then(result => result.data)
 * - api.post('/endpoint', payload).then(result => result.data)
 * - api.put('/endpoint', payload).then(result => result.data)
 * - api.delete('/endpoint').then(result => result.data)
 */

import toast from 'react-hot-toast';

export interface ApiResponse<T = unknown> {
    data: T;
}

export interface ApiErrorResponse {
    error: string;
}

export class ApiError extends Error {
    public statusCode: number;
    public errorDetails?: string;

    constructor(message: string, statusCode: number, errorDetails?: string) {
        super(message);
        this.name = 'ApiError';
        this.statusCode = statusCode;
        this.errorDetails = errorDetails;
    }
}

class ApiClient {
    private async request<T>(
        url: string,
        options: RequestInit = {},
        showToastOnError: boolean = true,
        customHeaders?: Record<string, string>,
        externalSignal?: AbortSignal
    ): Promise<ApiResponse<T>> {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000); // 30s timeout

        // Let callers cancel the request (e.g. user switched category/filter) by
        // chaining their signal into our internal timeout controller.
        const onExternalAbort = () => controller.abort();
        if (externalSignal) {
            if (externalSignal.aborted) {
                controller.abort();
            } else {
                externalSignal.addEventListener('abort', onExternalAbort);
            }
        }
        const cleanup = () => {
            clearTimeout(timeoutId);
            externalSignal?.removeEventListener('abort', onExternalAbort);
        };

        try {
            const response = await fetch(url, {
                ...options,
                signal: controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    ...customHeaders,
                    ...options.headers,
                },
            });

            cleanup();

            // Handle non-OK responses
            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                let errorDetails: string | undefined;

                try {
                    const errorData: ApiErrorResponse = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                        errorDetails = errorData.error;
                    }
                } catch {
                    // If we can't parse error JSON, use default message
                }

                const apiError = new ApiError(errorMessage, response.status, errorDetails);

                if (showToastOnError) {
                    toast.error(errorMessage);
                }

                throw apiError;
            }

            // Handle successful responses
            let result: ApiResponse<T>;

            const responseText = await response.text();

            if (!responseText.trim()) {
                result = { data: undefined as unknown as T };
            } else {
                result = JSON.parse(responseText);

                // Verify the response has the expected structure if it has body
                if (typeof result !== 'object' || !('data' in result)) {
                    const error = new ApiError('Invalid response format from server', 200);
                    if (showToastOnError) {
                        toast.error('Неверный формат ответа сервера');
                    }
                    throw error;
                }
            }

            return result;

        } catch (error) {
            cleanup();

            // Re-throw ApiErrors as-is
            if (error instanceof ApiError) {
                throw error;
            }

            // Caller-initiated cancellation: propagate the AbortError quietly
            // (no toast) so the now-stale handler can simply bail out.
            if (externalSignal?.aborted && error instanceof Error && error.name === 'AbortError') {
                throw error;
            }

            // Handle network/fetch errors
            let errorMessage = 'Network error occurred';
            if (error instanceof Error) {
                if (error.name === 'AbortError') {
                    errorMessage = 'Request timed out';
                } else {
                    errorMessage = error.message || 'Network error';
                }
            }

            const apiError = new ApiError(errorMessage, 0);

            if (showToastOnError) {
                toast.error(errorMessage);
            }

            throw apiError;
        }
    }

/**
 * GET request
 * @param url - API endpoint URL (will be prefixed with /api if not absolute)
 * @param showToastOnError - Whether to show error toast (default: true)
 */
async get<T>(url: string, customHeaders: Record<string, string> = {}, showToastOnError: boolean = true, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(url, { method: 'GET' }, showToastOnError, customHeaders, signal);
}

/**
 * POST request
 * @param url - API endpoint URL
 * @param data - Request payload
 * @param showToastOnError - Whether to show error toast (default: true)
 */
async post<T>(url: string, data?: unknown, customHeaders: Record<string, string> = {}, showToastOnError: boolean = true, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
    }, showToastOnError, customHeaders, signal);
}

/**
 * PUT request
 * @param url - API endpoint URL
 * @param data - Request payload
 * @param showToastOnError - Whether to show error toast (default: true)
 */
async put<T>(url: string, data?: unknown, customHeaders: Record<string, string> = {}, showToastOnError: boolean = true, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(url, {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
    }, showToastOnError, customHeaders, signal);
}

/**
 * DELETE request
 * @param url - API endpoint URL
 * @param showToastOnError - Whether to show error toast (default: true)
 */
async delete<T>(url: string, customHeaders: Record<string, string> = {}, showToastOnError: boolean = true, signal?: AbortSignal): Promise<ApiResponse<T>> {
    return this.request<T>(url, { method: 'DELETE' }, showToastOnError, customHeaders, signal);
}
}

const apiClient = new ApiClient();
export default apiClient;

export { ApiClient as ApiClientClass, apiClient as api };
